import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Availability } from '../../entities/Availability';
import { LanguageOptions } from '../../entities/LanguageOptions';
import { apiRequests } from '../../shared/api/apiRequests';
import { CreateContent } from '../../features/CreateContent';
import { Guid } from 'js-guid';
import { CONTENT_ITEM_KEYS } from '../../features/CreateContent/constants';
import { toast } from 'react-toastify';
import { UploadPicture } from '../../features/UploadPicture/index.js';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { Form, Row, Col } from 'react-bootstrap';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';

const CreatePortfolioPage = () => {
  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  
  const [form, setForm] = useState({
    title: '',
    shortDescription: '',
    categoryId: 0,
    industryId: 0,
    slug: '',
    mainLanguageId: 1,
    content: { items: [] },
    availability: {
      availableInAllCountries: true,
      allowedCountriesIds: [],
      disallowedCountriesIds: [],
    },
    SEOMetatags: {
      Title: '',
      Description: '',
      Keywords: '',
      title: '',
      description: '',
      keywords: '',
    },
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [categoriesRes, industriesRes] = await Promise.all([
          apiRequests.portfolio.getCategories(),
          apiRequests.portfolio.getIndustries(),
        ]);
        
        setCategories(categoriesRes.items || []);
        setIndustries(industriesRes.items || []);
        
        setForm((prev) => ({
          ...prev,
          categoryId: categoriesRes.items?.[0]?.id || 0,
          industryId: industriesRes.items?.[0]?.id || 0,
        }));
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
        toast.error('Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!form.title.trim()) {
      toast.error('Введите название портфолио');
      return;
    }
    
    if (!mainImage) {
      toast.error('Необходимо загрузить изображение');
      return;
    }

    setSaving(true);
    const body = new FormData();
    body.append('mainImg', mainImage, mainImage.name || 'image.jpg');

    try {
      const bodyItems = form.content.items.map((i) => {
        switch (i.discriminator) {
          case 'TextContentItem':
            return i;
          case 'ImageSliderContentItem':
            const items = i.images.map((z) => {
              const guid = Guid.newGuid().toString();
              body.append(guid, z.imgPath);
              return { imgPath: '', guid, discriminator: 'ImageContentItem' };
            });
            return { ...i, images: items };
          default:
            body.append(i.guid, i[CONTENT_ITEM_KEYS[i.discriminator]]);
            return { ...i, [CONTENT_ITEM_KEYS[i.discriminator]]: '' };
        }
      });

      const bodyModel = JSON.parse(JSON.stringify(form));
      bodyModel.content.items = bodyItems;
      body.append('model', JSON.stringify(bodyModel));

      await apiRequests.portfolio.create(body);
      toast.success('Портфолио успешно создано');
      setTimeout(() => navigate('/portfolio'), 1500);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Произошла ошибка при создании';
      toast.error(errorMessage);
      console.error('Ошибка:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddContent = (value) => {
    setForm((prev) => ({
      ...prev,
      content: { items: [...prev.content.items, { guid: value.id, discriminator: value.type }] },
    }));
  };

  const handleChangeContent = (value) => {
    setForm((prev) => {
      const newContentItems = prev.content.items.map((i) => {
        if (i.guid === value.id) {
          return { ...i, [CONTENT_ITEM_KEYS[value.type]]: value.content };
        }
        return i;
      });
      return { ...prev, content: { items: newContentItems } };
    });
  };

  const handleDeleteContent = (contentId) => {
    setForm((prev) => ({
      ...prev,
      content: { items: prev.content.items.filter((i) => i.guid !== contentId) },
    }));
  };

  if (loading) return <FormLoading />;

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate('/portfolio')}
      saving={saving}
      submitText="Создать портфолио"
      savingText="Создание..."
    >
      {/* Основная информация */}
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <Form.Group className="mb-4">
              <Form.Label className="fw-medium">Название портфолио <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Введите название"
                required
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-medium">Краткое описание</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.shortDescription}
                onChange={(e) => setForm((prev) => ({ ...prev, shortDescription: e.target.value }))}
                placeholder="Введите краткое описание проекта"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label className="fw-medium">Категория</Form.Label>
                  <Form.Select
                    value={form.categoryId}
                    onChange={(e) => setForm((prev) => ({ ...prev, categoryId: Number(e.target.value) }))}
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.title}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-4">
                  <Form.Label className="fw-medium">Индустрия</Form.Label>
                  <Form.Select
                    value={form.industryId}
                    onChange={(e) => setForm((prev) => ({ ...prev, industryId: Number(e.target.value) }))}
                  >
                    {industries.map((ind) => (
                      <option key={ind.id} value={ind.id}>{ind.title}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Col>

          <Col lg={4}>
            <Form.Group className="mb-4">
              <Form.Label className="fw-medium">Изображение <span className="text-danger">*</span></Form.Label>
              <UploadPicture onSelect={setMainImage} entityType="portfolio" />
            </Form.Group>
          </Col>
        </Row>
      </FormSection>

      {/* Контент */}
      <FormSection title="Контент страницы">
        <CreateContent
          onDelete={handleDeleteContent}
          onChange={handleChangeContent}
          onAdd={handleAddContent}
        />
      </FormSection>

      {/* Настройки */}
      <FormSection title="Настройки">
        <Row>
          <Col lg={6}>
            <LanguageOptions
              onSelect={(val) => setForm((prev) => ({ ...prev, mainLanguageId: val }))}
            />
          </Col>
        </Row>

        <div className="mt-4">
          <Availability
            onSelect={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, availableInAllCountries: val },
              }))
            }
            onSelectAllowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, allowedCountriesIds: val },
              }))
            }
            onSelectDisallowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, disallowedCountriesIds: val },
              }))
            }
          />
        </div>
      </FormSection>

      {/* SEO */}
      <FormSection title="SEO настройки">
        <SEOMetatagsForm
          seoMetatags={form.SEOMetatags}
          onChange={(metaTags) => setForm((prev) => ({ ...prev, SEOMetatags: metaTags }))}
        />
      </FormSection>
    </FormLayout>
  );
};

export default CreatePortfolioPage;
